import React, { useState, useMemo } from 'react'
import config from '../../config.js'
import { apiErrorMessage } from '../../utils/apiError';
import { QUOTE_SELL_RATE_FIELDS, newQuoteRateSetup, quoteRatesToPayload, quoteHeaderToPayload, describeQuoteRates } from '../../utils/quoteRateDefaults';

const API = config.API_BASE_URL
const NESTER = config.NESTER_API_URL

// ── Shared helpers ──

function defaultProductId(fileName) {
  if (!fileName) return ''
  return fileName.replace(/\.dxf$/i, '').replace(/\s+/g, '-').toUpperCase()
}

function formatDim(val) {
  if (val == null) return '0'
  return val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)
}

function findMaterial(allMaterials, matType, thicknessId, surfaceFinish, sheetW, sheetL, customerSupplied, custId) {
  if (!matType || !thicknessId) return null
  return allMaterials.find(m => {
    if (m.material_type !== matType) return false
    if (m.thickness_id !== parseInt(thicknessId)) return false
    if (surfaceFinish && m.surface_finish !== surfaceFinish) return false
    if (!surfaceFinish && m.surface_finish) return false
    if (sheetW && m.width && Math.abs(m.width - parseFloat(sheetW)) > 0.1) return false
    if (sheetL && m.length && Math.abs(m.length - parseFloat(sheetL)) > 0.1) return false
    if (customerSupplied && custId && m.owner_customer_id !== parseInt(custId)) return false
    return true
  }) || null
}

// ── Material densities (lbs/in³) ──

const DENSITY_LBS = {
  'mild steel a36': 0.2836,
  'stainless steel': 0.2890,
  'aluminum': 0.0975,
  'copper': 0.3230,
  'brass': 0.3070,
}
const DEFAULT_DENSITY = 0.2836

function getDensity(materialType) {
  if (!materialType) return DEFAULT_DENSITY
  const key = materialType.toLowerCase()
  return DENSITY_LBS[key] || DEFAULT_DENSITY
}

// ── Material pricing methods ──

const PRICING_METHODS = [
  { value: 'from_nest_sheet', label: 'From Nest Sheet', desc: 'Allocate 100% of sheet cost across parts by area (recommended for nester)' },
  { value: 'by_weight', label: 'By Weight', desc: 'Part weight x $/lb from CWT pricing' },
  { value: 'by_bounding_area', label: 'By Bounding Area', desc: 'Bounding rectangle x thickness x density x $/lb' },
  { value: 'by_solid_profile', label: 'By Solid Profile', desc: 'Actual part area (minus holes) x thickness x density x $/lb' },
  { value: 'customer_supplied', label: 'Customer Supplied', desc: 'No material cost ($0)' },
]

/**
 * Calculate per-part material cost based on selected pricing method.
 * Returns cost per single unit of the part.
 */
function calcMaterialCostPerPart(method, part, {
  costPerSheet, costPerLb, thicknessIn, materialType, sheetsNeeded,
  allPartsSummary, // array of { part_id, part_area_sq_in, quantity }
}) {
  const density = getDensity(materialType)
  const area = part.part_area_sq_in || 0
  const w = part.bounding_width || 0
  const h = part.bounding_height || 0

  switch (method) {
    case 'from_nest_sheet': {
      // Total sheet cost divided proportionally by part area
      if (!costPerSheet || !sheetsNeeded) return 0
      const totalSheetCost = costPerSheet * sheetsNeeded
      const totalAreaQty = (allPartsSummary || []).reduce(
        (sum, ps) => sum + (ps.part_area_sq_in || 0) * (ps.quantity || 1), 0
      )
      if (totalAreaQty <= 0) return 0
      return (area * totalSheetCost) / totalAreaQty
    }

    case 'by_weight': {
      if (!thicknessIn || !costPerLb) return 0
      const weight = area * thicknessIn * density
      return weight * costPerLb
    }

    case 'by_bounding_area': {
      if (!thicknessIn || !costPerLb) return 0
      const boundingArea = Math.max(w, h) * Math.min(w, h)
      const weight = boundingArea * thicknessIn * density
      return weight * costPerLb
    }

    case 'by_solid_profile': {
      if (!thicknessIn || !costPerLb) return 0
      const weight = area * thicknessIn * density
      return weight * costPerLb
    }

    case 'customer_supplied':
      return 0

    default:
      return 0
  }
}

// ── Per-part time passthrough ──
// As of Apr 2026, the dxf-nester-api parts_summary ALREADY allocates all 8
// sheet-level overhead components (SH Cut, Move, Pierce cycle overhead,
// Nozzle Clean, Alignment, Shuttle, Other) proportionally into each part's
// est_process_time_sec by beam-on weight, so:
//
//   Σ(parts_summary[i].est_process_time_sec × quantity) = detailed_estimate.total_sec
//
// This function is now a thin reshape: take est_process_time_sec as the full
// per-part laser time, and beam_on_sec (legacy parts may not have it — fall
// back to a zero so gas/consumable cost calc skips the per-line beam-on path
// and uses laser-minutes instead). Earlier versions hand-allocated only 4 of
// the 8 components and missed Nozzle Clean / Alignment / Pierce cycle overhead;
// see CLAUDE.md for the full history.
function allocatePartTimes(nestResultOrGroup, partsSummary) {
  if (!partsSummary || partsSummary.length === 0) return {}
  const allocations = {}
  for (const ps of partsSummary) {
    const fullPerUnitSec = ps.est_process_time_sec || 0
    const beamPerUnitSec = ps.beam_on_sec != null ? ps.beam_on_sec : 0
    allocations[ps.part_id] = {
      beamOnMinutes: Math.round((beamPerUnitSec / 60) * 100) / 100,
      laserMinutes: Math.round((fullPerUnitSec / 60) * 100) / 100,
    }
  }
  return allocations
}

// ── Build quote line rows from nester data ──

function buildQuoteRows(nestResult, parts, materialGroups, allMaterials, allParts, thicknesses,
  projMatType, projThickDisp, projSF, projCustId, projSheetW, projSheetL, projCustSupplied,
  projCostPerSheet, projCostPerLb, projThicknessIn) {

  const rows = []

  const groupResults = nestResult?.group_results || []
  const nestSources = groupResults.length > 0
    ? groupResults.map(gr => ({
        result: gr,
        group: materialGroups?.find(g => g.group_id === gr.material_group_id)
      }))
    : [{ result: nestResult, group: null }]

  for (const { result: nr, group } of nestSources) {
    if (!nr) continue

    const matType = group?.material_type || projMatType || ''
    const thickDisp = group?.thickness_display || projThickDisp || ''
    const surfFinish = group?.surface_finish || projSF || ''
    const sheetW = group?.sheet_width || projSheetW || ''
    const sheetL = group?.sheet_length || projSheetL || ''
    const custSupplied = group?.customer_supplied_material || projCustSupplied || false
    const custId = projCustId || ''
    const costPerSheet = parseFloat(group?.cost_per_sheet) || parseFloat(projCostPerSheet) || 0
    const costPerLb = parseFloat(group?.cost_per_lb) || parseFloat(projCostPerLb) || 0
    const thicknessIn = parseFloat(group?.thickness_in) || parseFloat(projThicknessIn) || 0

    const matchedThickness = thicknesses.find(t =>
      t.display_thickness === thickDisp &&
      t.material_type?.toLowerCase() === matType.toLowerCase()
    )
    const thickId = matchedThickness?.thickness_id || null

    const materialMatch = findMaterial(allMaterials, matType, thickId, surfFinish, sheetW, sheetL, custSupplied, custId)

    const partsSummary = nr.parts_summary || []
    const timeAlloc = allocatePartTimes(nr, partsSummary)
    const sheetsNeeded = nr.sheets_needed || 0

    // Build summary for area-proportional calc (all parts in this group)
    const groupParts = group
      ? parts.filter(p => p.material_group_id === group.group_id)
      : parts

    const allPartsSummary = groupParts.map(p => ({
      part_id: p.part_id,
      part_area_sq_in: p.part_area_sq_in || 0,
      quantity: p.quantity || 1,
    }))

    for (const part of groupParts) {
      const productId = defaultProductId(part.file_name)
      const jobqPart = allParts.find(p => p.product_id === productId)
      const w = part.bounding_width
      const h = part.bounding_height
      const sizeLength = w && h ? Math.max(w, h) : null
      const sizeWidth = w && h ? Math.min(w, h) : null
      const times = timeAlloc[part.part_id] || { beamOnMinutes: 0, laserMinutes: 0 }

      rows.push({
        nesterPartId: part.part_id,
        productId,
        description: sizeLength && sizeWidth ? `${formatDim(sizeLength)} x ${formatDim(sizeWidth)}` : '',
        jobqPartId: jobqPart?.part_id || null,
        partFound: !!jobqPart,
        materialId: materialMatch?.material_id || null,
        materialType: matType,
        thicknessId: thickId,
        surfaceFinish: surfFinish,
        isCustomerSupplied: custSupplied,
        sizeLength,
        sizeWidth,
        quantity: part.quantity || 1,
        processSteps: 'Laser',
        laserMinutes: times.laserMinutes,
        beamOnMinutes: times.beamOnMinutes,
        // Material pricing data (used by calcMaterialCostPerPart)
        partAreaSqIn: part.part_area_sq_in || 0,
        boundingWidth: w || 0,
        boundingHeight: h || 0,
        costPerSheet,
        costPerLb,
        thicknessIn,
        sheetsNeeded,
        allPartsSummary,
        // Filled after cost calculation
        manualMaterialPrice: null,
        price: null,
        costBreakdown: null,
        totalCost: null,
        marginPct: null,
        status: 'pending',
        error: null,
        groupName: group?.group_name || null,
      })
    }
  }
  return rows
}

// ── Component ──

export default function CreateQuoteModal({
  show, onClose, nestResult, project, materialGroups, parts,
  allMaterials, allParts, thicknesses, surfaceFinishes, customers,
  projectMaterialType, projectThicknessDisplay, projectSurfaceFinish,
  projectCustomerId, projectSheetWidth, projectSheetLength, projectCustomerSupplied,
  projectCostPerSheet, projectCostPerLb, projectThicknessIn,
}) {
  // Quote rates — the per-quote pricing lever (decision 2, 2026-09-02). All
  // six sell rates + the material markup are editable here and seeded from
  // the SAME constant the Quote page's "New" button uses, so a nester quote
  // and a hand-built quote start from identical numbers. State holds what
  // was typed ($/hr; percent for the markup); quoteRatesToPayload /
  // quoteHeaderToPayload convert for the API at send time.
  const [rates, setRates] = useState(() => newQuoteRateSetup())
  const setRate = (key, raw) => setRates(prev => ({ ...prev, [key]: raw }))
  // Required for the resulting quote (and the SO it converts to). Pre-fills
  // from customer's default_fulfillment_type when known. Operator must
  // pick before saving — validation blocks empty.
  const [fulfillmentType, setFulfillmentType] = useState('')

  // Material pricing method
  const [pricingMethod, setPricingMethod] = useState('from_nest_sheet')

  // Stage: 'setup' → 'preview' → 'done'
  const [stage, setStage] = useState('setup')
  const [calculating, setCalculating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedQuoteId, setSavedQuoteId] = useState(null)

  // Build rows from nester data
  const initialRows = useMemo(() => {
    if (!nestResult || !parts || parts.length === 0) return []
    return buildQuoteRows(
      nestResult, parts, materialGroups, allMaterials, allParts, thicknesses,
      projectMaterialType, projectThicknessDisplay, projectSurfaceFinish,
      projectCustomerId, projectSheetWidth, projectSheetLength,
      projectCustomerSupplied, projectCostPerSheet, projectCostPerLb, projectThicknessIn
    )
  }, [nestResult, parts, materialGroups, allMaterials, allParts, thicknesses,
      projectMaterialType, projectThicknessDisplay, projectSurfaceFinish,
      projectCustomerId, projectSheetWidth, projectSheetLength, projectCustomerSupplied,
      projectCostPerSheet, projectCostPerLb, projectThicknessIn])

  const [rows, setRows] = useState(initialRows)

  const customerName = useMemo(() => {
    if (!projectCustomerId) return null
    const cust = customers?.find(c => c.customer_id === parseInt(projectCustomerId))
    return cust?.name || null
  }, [projectCustomerId, customers])

  // Pre-fill fulfillment from the project's customer default. Runs once on
  // open / customer change; doesn't clobber an explicit operator pick.
  React.useEffect(() => {
    if (!projectCustomerId || fulfillmentType) return
    const cust = customers?.find(c => c.customer_id === parseInt(projectCustomerId))
    if (cust?.default_fulfillment_type) {
      setFulfillmentType(cust.default_fulfillment_type)
    }
    // intentionally only fires when customer changes; operator override sticks
  }, [projectCustomerId, customers, fulfillmentType])

  const missingParts = rows.filter(r => !r.partFound)
  const hasCustomer = !!projectCustomerId

  // Compute preview material costs per part for selected method
  const materialPreview = useMemo(() => {
    const preview = {}
    for (const row of rows) {
      preview[row.nesterPartId] = Math.round(calcMaterialCostPerPart(pricingMethod, {
        part_area_sq_in: row.partAreaSqIn,
        bounding_width: row.boundingWidth,
        bounding_height: row.boundingHeight,
      }, {
        costPerSheet: row.costPerSheet,
        costPerLb: row.costPerLb,
        thicknessIn: row.thicknessIn,
        materialType: row.materialType,
        sheetsNeeded: row.sheetsNeeded,
        allPartsSummary: row.allPartsSummary,
      }) * 100) / 100
    }
    return preview
  }, [rows, pricingMethod])

  // ── Calculate costs ──

  async function calculateCosts() {
    setCalculating(true)
    const updatedRows = [...rows]

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i]
      try {
        // Calculate material cost per selected method
        const matCost = calcMaterialCostPerPart(pricingMethod, {
          part_area_sq_in: row.partAreaSqIn,
          bounding_width: row.boundingWidth,
          bounding_height: row.boundingHeight,
        }, {
          costPerSheet: row.costPerSheet,
          costPerLb: row.costPerLb,
          thicknessIn: row.thicknessIn,
          materialType: row.materialType,
          sheetsNeeded: row.sheetsNeeded,
          allPartsSummary: row.allPartsSummary,
        })

        const payload = {
          part_id: row.jobqPartId || null,
          size_length: row.sizeLength || 0,
          size_width: row.sizeWidth || 0,
          material_id: row.materialId || null,
          material_type: row.materialType || null,
          thickness_id: row.thicknessId || null,
          surface_finish: row.surfaceFinish || null,
          is_customer_supplied_material: pricingMethod === 'customer_supplied',
          manual_material_price: pricingMethod === 'customer_supplied' ? 0 : Math.round(matCost * 100) / 100,
          process_steps: 'Laser',
          initial_est_laser_minutes: row.laserMinutes || 0,
          beam_on_minutes: row.beamOnMinutes || 0,
          // six sell rates ($/hr), material_markup (fraction), coating_markup (null = follows markup)
          ...quoteRatesToPayload(rates),
        }

        const res = await fetch(`${API}/quotes/calculate-part-cost`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, "Cost calc failed"));
        }

        const result = await res.json()
        updatedRows[i] = {
          ...row,
          manualMaterialPrice: Math.round(matCost * 100) / 100,
          price: Math.round((result.calculated_price || 0) * 100) / 100,
          costBreakdown: result.breakdown ? { breakdown: result.breakdown, total_cost: result.total_cost, calculated_price: result.calculated_price } : null,
          totalCost: result.total_cost || 0,
          marginPct: result.profit_margin != null ? Math.round(result.profit_margin * 10) / 10 : null,
          status: 'calculated',
          error: null,
        }
      } catch (err) {
        updatedRows[i] = { ...row, status: 'error', error: err.message }
      }
    }

    setRows(updatedRows)
    setCalculating(false)
    setStage('preview')
  }

  // ── Create quote ──

  async function createQuote() {
    setSaving(true)
    try {
      const lines = rows.map(row => ({
        product_id: row.productId,
        part_id: row.jobqPartId || null,
        description: row.description,
        material_id: row.materialId || null,
        material_type: row.materialType || null,
        thickness_id: row.thicknessId || null,
        surface_finish: row.surfaceFinish || null,
        is_customer_supplied_material: pricingMethod === 'customer_supplied',
        size_length: row.sizeLength || 0,
        size_width: row.sizeWidth || 0,
        price: row.price || 0,
        quantity: row.quantity,
        is_quick_quote_item: false,
        process_steps: 'Laser',
        initial_est_laser_minutes: row.laserMinutes || 0,
        beam_on_minutes: row.beamOnMinutes || 0,
        cost_breakdown: row.costBreakdown || null,
      }))

      // Required: fulfillment type must be picked before save so it locks
      // in at quote time and inherits to the SO at convert-to-sales-order.
      if (!fulfillmentType) {
        alert('Fulfillment is required — pick Carrier Shipment, Customer Pickup, or We Deliver before saving.')
        setSaving(false)
        return
      }

      const payload = {
        customer_id: parseInt(projectCustomerId),
        nester_project_id: project?.project_id || null,
        notes: `From nester project: ${project?.project_name || 'Unknown'}`,
        // six sell rates ($/hr), material_markup + coating_markup + target_margin as fractions
        ...quoteHeaderToPayload(rates),
        shipping_amount: 0,
        additional_fees: 0,
        tax_amount: 0,
        discount_amount: 0,
        fulfillment_type: fulfillmentType,
        lines,
      }

      const res = await fetch(`${API}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Create quote failed"));
      }

      const result = await res.json()
      setSavedQuoteId(result.quote_id)

      // Update nester project status to "quoted"
      try {
        await fetch(`${NESTER}/projects/${project.project_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'quoted' }),
        })
      } catch (e) {
        console.warn('Failed to update nester project status:', e)
      }

      setStage('done')
    } catch (err) {
      alert(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Price override ──
  function updatePrice(idx, val) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, price: parseFloat(val) || 0 } : r))
  }

  // ── Totals ──
  const subtotal = rows.reduce((s, r) => s + (r.price || 0) * r.quantity, 0)
  const totalCost = rows.reduce((s, r) => s + (r.totalCost || 0) * r.quantity, 0)
  const overallMargin = subtotal > 0 ? Math.round(((subtotal - totalCost) / subtotal) * 1000) / 10 : 0

  if (!show) return null

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={s.header}>
          <h5 style={{ margin: 0, fontSize: '1rem' }}>
            {stage === 'done' ? 'Quote Created' : 'Create Quote from Nester'}
          </h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div style={s.body}>
          {/* Warnings */}
          {!hasCustomer && (
            <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: '0.78rem' }}>
              No customer selected on this nester project. A customer is required to create a quote.
            </div>
          )}
          {missingParts.length > 0 && (
            <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: '0.78rem' }}>
              {missingParts.length} part{missingParts.length > 1 ? 's' : ''} not found in JobQ
              ({missingParts.map(p => p.productId).join(', ')}).
              Create parts first for best cost accuracy.
            </div>
          )}

          {/* Stage: Setup */}
          {stage === 'setup' && (
            <>
              {/* Project info */}
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', fontSize: '0.82rem' }}>
                <div><strong>Project:</strong> {project?.project_name}</div>
                <div><strong>Customer:</strong> {customerName || <span style={{ color: '#dc2626' }}>None</span>}</div>
                <div><strong>Parts:</strong> {rows.length}</div>
                <div><strong>Total Qty:</strong> {rows.reduce((s, r) => s + r.quantity, 0)}</div>
              </div>

              {/* Quote rates (decision 2): all six sell rates + material markup, editable,
                  seeded from the shared QUOTE_RATE_DEFAULTS the Quote page's New button uses. */}
              <div style={{
                border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.75rem',
                marginBottom: '0.75rem', backgroundColor: '#f8fafc',
              }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.4rem', color: '#334155' }}>
                  Quote rates <span style={{ fontWeight: 400, color: '#64748b' }}>— sell $/hr per station · material markup %</span>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {QUOTE_SELL_RATE_FIELDS.map(([key, label]) => (
                    <label key={key} style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      {label} $/hr
                      <input type="number" min="0" step="1" className="form-control form-control-sm"
                        value={rates[key]} onChange={e => setRate(key, e.target.value)}
                        style={{ width: 72, fontSize: '0.78rem' }} />
                    </label>
                  ))}
                  <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    Material Markup %
                    <input type="number" min="0" step="1" className="form-control form-control-sm"
                      value={rates.material_markup} onChange={e => setRate('material_markup', e.target.value)}
                      style={{ width: 64, fontSize: '0.78rem' }} />
                  </label>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.3rem' }}>
                  Per-quote rates are the pricing lever — they can be changed on the quote afterwards. Saved on the quote header and used for every line the engine prices. Defaults: {describeQuoteRates()}.
                </div>
              </div>

              {/* Fulfillment row */}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  Fulfillment <span style={{ color: '#dc2626' }}>*</span>
                  <select
                    className="form-select form-select-sm"
                    value={fulfillmentType}
                    onChange={e => setFulfillmentType(e.target.value)}
                    style={{ width: 170, fontSize: '0.78rem', borderColor: !fulfillmentType ? '#dc2626' : undefined }}
                    title="Required. Locks at quote time and inherits to the SO at conversion. Pickup orders use the Mark Ready for Pickup → Confirm Pickup workflow."
                  >
                    <option value="">— Select —</option>
                    <option value="shipping">Carrier Shipment</option>
                    <option value="pickup">Customer Pickup</option>
                    <option value="delivery">We Deliver</option>
                  </select>
                </label>
              </div>

              {/* Material Pricing Method */}
              <div style={{
                border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.75rem',
                marginBottom: '0.75rem', backgroundColor: '#f8fafc',
              }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.4rem', color: '#334155' }}>
                  Material Pricing Method
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem' }}>
                  {PRICING_METHODS.map(pm => (
                    <label key={pm.value} style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      fontSize: '0.76rem', cursor: 'pointer',
                      color: pricingMethod === pm.value ? '#1e40af' : '#475569',
                      fontWeight: pricingMethod === pm.value ? 600 : 400,
                    }}>
                      <input type="radio" name="pricingMethod"
                        value={pm.value} checked={pricingMethod === pm.value}
                        onChange={() => setPricingMethod(pm.value)} />
                      {pm.label}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.3rem' }}>
                  {PRICING_METHODS.find(p => p.value === pricingMethod)?.desc}
                </div>
              </div>

              {/* Parts table */}
              <table className="table table-sm" style={{ fontSize: '0.78rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th>Product ID</th>
                    <th style={{ textAlign: 'center' }}>In JobQ</th>
                    <th style={{ textAlign: 'right' }}>L x W</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Mat. Cost</th>
                    <th style={{ textAlign: 'right' }}>Beam-On</th>
                    <th style={{ textAlign: 'right' }}>Laser Time</th>
                    {rows.some(r => r.groupName) && <th>Group</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 600 }}>{row.productId}</td>
                      <td style={{ textAlign: 'center' }}>
                        {row.partFound
                          ? <span style={{ color: '#16a34a' }}>&#10003;</span>
                          : <span style={{ color: '#f59e0b' }} title="Part not found in JobQ">&#9888;</span>
                        }
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.sizeLength ? `${formatDim(row.sizeLength)} x ${formatDim(row.sizeWidth)}` : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{row.quantity}</td>
                      <td style={{ textAlign: 'right', color: '#1e40af' }}>
                        ${(materialPreview[row.nesterPartId] || 0).toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{row.beamOnMinutes.toFixed(2)}m</td>
                      <td style={{ textAlign: 'right' }}>{row.laserMinutes.toFixed(2)}m</td>
                      {rows.some(r => r.groupName) && (
                        <td style={{ fontSize: '0.72rem', color: '#64748b' }}>{row.groupName || '-'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Stage: Preview */}
          {stage === 'preview' && (
            <>
              <div style={{ fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                <strong>Cost breakdown per part</strong> — adjust prices as needed, then create the quote.
              </div>
              <table className="table table-sm" style={{ fontSize: '0.78rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th>Product ID</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Material</th>
                    <th style={{ textAlign: 'right' }}>Laser</th>
                    <th style={{ textAlign: 'right' }}>Gas</th>
                    <th style={{ textAlign: 'right' }}>Consumable</th>
                    <th style={{ textAlign: 'right' }}>Cost</th>
                    <th style={{ textAlign: 'right', width: 90 }}>Price</th>
                    <th style={{ textAlign: 'right' }}>Margin</th>
                    <th style={{ textAlign: 'right' }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const bd = row.costBreakdown?.breakdown
                    const matCost = bd?.material?.cost || 0
                    const laserPrice = bd?.laser?.quoted_price || 0
                    const gasCost = bd?.laser?.details?.gas_cost || 0
                    const consCost = bd?.laser?.details?.consumable_cost || 0
                    const lineTotal = (row.price || 0) * row.quantity

                    return (
                      <tr key={idx} style={{
                        backgroundColor: row.status === 'error' ? '#fef2f2' : 'transparent'
                      }}>
                        <td style={{ fontWeight: 600 }}>{row.productId}</td>
                        <td style={{ textAlign: 'right' }}>{row.quantity}</td>
                        <td style={{ textAlign: 'right' }}>${matCost.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>${laserPrice.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>${gasCost.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>${consCost.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>${(row.totalCost || 0).toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <input type="number" className="form-control form-control-sm"
                            value={row.price ?? ''} onChange={e => updatePrice(idx, e.target.value)}
                            step="0.01" min="0"
                            style={{ fontSize: '0.76rem', padding: '1px 4px', width: 85, textAlign: 'right' }} />
                        </td>
                        <td style={{
                          textAlign: 'right',
                          color: (row.marginPct || 0) >= 30 ? '#16a34a' : (row.marginPct || 0) >= 15 ? '#f59e0b' : '#dc2626',
                          fontWeight: 600
                        }}>
                          {row.marginPct != null ? `${row.marginPct}%` : '-'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>${lineTotal.toFixed(2)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #334155', fontWeight: 700, fontSize: '0.82rem' }}>
                    <td colSpan={6}>Totals</td>
                    <td style={{ textAlign: 'right' }}>${totalCost.toFixed(2)}</td>
                    <td></td>
                    <td style={{
                      textAlign: 'right',
                      color: overallMargin >= 30 ? '#16a34a' : overallMargin >= 15 ? '#f59e0b' : '#dc2626',
                    }}>
                      {overallMargin}%
                    </td>
                    <td style={{ textAlign: 'right' }}>${subtotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </>
          )}

          {/* Stage: Done */}
          {stage === 'done' && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>&#10003;</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.5rem' }}>
                Quote #{savedQuoteId} Created
              </div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                {rows.length} line items, ${subtotal.toFixed(2)} total
              </div>
              <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Open the Quote Management page to review, adjust, and send to the customer.
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.5rem' }}>
                Rates saved on this quote: {describeQuoteRates(rates)} — change them in Quote Setup on the Quote page.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={s.footer}>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
            {stage === 'preview' && `${rows.length} lines | Subtotal: $${subtotal.toFixed(2)} | Margin: ${overallMargin}%`}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              {stage === 'done' ? 'Close' : 'Cancel'}
            </button>

            {stage === 'setup' && (
              <button className="btn btn-sm btn-primary" onClick={calculateCosts}
                disabled={!hasCustomer || rows.length === 0 || calculating}>
                {calculating ? 'Calculating...' : 'Calculate Costs'}
              </button>
            )}

            {stage === 'preview' && (
              <>
                <button className="btn btn-sm btn-outline-primary" onClick={() => setStage('setup')}>
                  Back
                </button>
                <button className="btn btn-sm btn-success" onClick={createQuote}
                  disabled={saving || rows.every(r => r.status === 'error')}>
                  {saving ? 'Creating...' : `Create Quote ($${subtotal.toFixed(2)})`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    backgroundColor: 'white', borderRadius: '12px', width: '1050px', maxWidth: '95vw',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  },
  header: {
    padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  body: {
    flex: 1, overflow: 'auto', padding: '0.75rem 1rem',
  },
  footer: {
    padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
}
